import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { ArrowUpRight, Radio } from "lucide-react";
import { ShareButton } from "@/components/ShareButton";

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

const REGIONS = [
  { id: "all", label: "All" },
  { id: "Siliguri", label: "Siliguri" },
  { id: "North Bengal", label: "N. Bengal" },
  { id: "West Bengal", label: "W. Bengal" },
  { id: "India", label: "India" },
];

export default function NewsPage() {
  const { data: news = [], isLoading } = useQuery({
    queryKey: ["news"], queryFn: () => api.get("/news").then((r) => r.data), staleTime: 300000,
  });

  const [region, setRegion] = useState("all");
  const filtered = useMemo(
    () => (region === "all" ? news : news.filter((n) => n.region === region)),
    [news, region],
  );
  const counts = useMemo(() => {
    const c = { all: news.length };
    for (const r of REGIONS) if (r.id !== "all") c[r.id] = news.filter((n) => n.region === r.id).length;
    return c;
  }, [news]);

  return (
    <div className="pt-32">
      <div className="container-px mx-auto max-w-[1600px] pb-24">
        <div className="flex items-center gap-3">
          <Radio className="h-4 w-4 animate-pulse text-cyan" />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan">Live · Real Estate Headlines</p>
        </div>
        <h1 className="mt-4 font-display text-5xl font-extrabold uppercase leading-none text-foreground md:text-8xl">Market News</h1>
        <p className="mt-6 max-w-2xl text-muted-foreground">
          Hyperlocal first — <strong>Siliguri</strong>, <strong>North Bengal</strong> &amp; <strong>West Bengal</strong> headlines pulled live, then the Indian market. Auto-refreshed every 15 minutes from Google News.
        </p>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="news-regions">
          {REGIONS.map((r) => (
            <button
              key={r.id}
              onClick={() => setRegion(r.id)}
              data-testid={`news-region-${r.id.replace(/\s+/g, "").toLowerCase()}`}
              className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                region === r.id
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-background text-muted-foreground hover:border-foreground hover:text-foreground"
              }`}
            >
              {r.label}
              <span className="ml-2 text-[9px] opacity-70">{counts[r.id] ?? 0}</span>
            </button>
          ))}
        </div>

        {isLoading ? (
          <p className="mt-16 font-mono text-sm text-muted-foreground">Fetching latest headlines…</p>
        ) : filtered.length === 0 ? (
          <p className="mt-16 font-mono text-sm text-muted-foreground" data-testid="news-empty">
            No fresh {region === "all" ? "" : `${region} `}headlines right now. Pull-cycle refreshes every 15 min.
          </p>
        ) : (
          <div className="mt-10 border border-border" data-testid="news-list">
            {filtered.map((n, i) => (
              <Reveal key={`${region}-${i}`} delay={Math.min(i, 8) * 0.04}>
                <a href={n.link} target="_blank" rel="noreferrer" data-testid={`news-item-${i}`}
                  className="group flex items-start justify-between gap-6 border-b border-border p-6 transition-colors last:border-0 hover:bg-card">
                  <div className="flex gap-6">
                    <span className="font-mono text-sm text-cyan">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="max-w-3xl font-display text-lg font-bold uppercase leading-tight text-foreground group-hover:text-cyan md:text-2xl">{n.title}</h3>
                      <p className="mt-2 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        {n.region && (
                          <span className="rounded-full border border-cyan/40 px-2 py-0.5 text-cyan">{n.region}</span>
                        )}
                        <span>{n.source}</span>
                        <span>·</span>
                        <span>{timeAgo(n.pubDate)}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <ShareButton title={n.title} url={n.link} note={`Real estate news via ${n.source}`}
                      label="" testid={`news-share-${i}`}
                      className="text-muted-foreground hover:text-cyan" />
                    <ArrowUpRight className="h-5 w-5 shrink-0 text-muted-foreground transition-all group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-cyan" />
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
